/**
 * Admin — Bulk Kit Orders (Feb 2026).
 *
 * List of every partner bulk order + drawer with Generate Labels /
 * Mark Shipped / Cancel / Refund / Download Packing Sheet actions.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Package, RefreshCw, Loader2, Printer, Truck, X, DollarSign,
  Download, Ban, AlertTriangle, Archive, ArchiveRestore, Receipt, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";

const CENTS = (n) => `$${((n || 0) / 100).toFixed(2)}`;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Feb 2026 (SCWT-BULK-KIT-RECEIPT) — receipt state chip helper used in
// the drawer AND the table cell.
function receiptChipLabel(order) {
  const st = order?.receipt_send_status;
  if (st === "sent" && order?.receipt_sent_at) {
    return { label: `Sent ${(order.receipt_sent_at || "").slice(0, 10)}`, tone: "sent" };
  }
  if (st === "failed") return { label: "Failed", tone: "failed" };
  return { label: "Not sent", tone: "missing" };
}
function receiptChipClasses(tone) {
  if (tone === "sent")    return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (tone === "failed")  return "bg-rose-50 text-rose-800 border-rose-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
}

// Feb 2026 (SCWT-52B41C follow-up + prod-sync fix) — map badge bucket
// keys to a concrete `status` filter. The real bulk-order status
// ladder is `pending_payment → awaiting_labels → labels_generated →
// packing → shipped` — there is no `paid` status. Earlier this
// mapped to `paid` which returned 0 rows.
const BUCKET_TO_STATUS = {
  ready_to_pack:    "labels_generated",
  labels_to_create: "awaiting_labels",
};

export default function AdminBulkKitOrders() {
  const [searchParams] = useSearchParams();
  const bucket = searchParams.get("bucket");
  const initialStatus = (bucket && BUCKET_TO_STATUS[bucket]) || "";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  // Feb 2026 — hide cancelled + archived by default so the ops list
  // stays focused. Toggles let admins pull them back into view.
  const [includeArchived, setIncludeArchived] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (includeArchived) params.set("include_archived", "true");
      if (includeCancelled) params.set("include_cancelled", "true");
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data } = await api.get(`/admin/bulk-kit-orders${qs}`);
      setOrders(data.items || []);
    } catch { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, [statusFilter, includeArchived, includeCancelled]);

  const archiveOrder = async (orderId, unarchive = false) => {
    const label = unarchive ? "un-archive" : "archive";
    if (!confirm(`${unarchive ? "Un-archive" : "Archive"} this bulk order?`)) return;
    try {
      const suffix = unarchive ? "unarchive" : "archive";
      await api.post(`/admin/bulk-kit-orders/${orderId}/${suffix}`, {});
      toast.success(`Order ${label}d.`);
      reload();
    } catch (e) {
      toast.error(`Failed to ${label}: ${e.response?.data?.detail || e.message}`);
    }
  };

  // Feb 2026 (SCWT-BULK-KIT-RECEIPT) — Historical backfill: preview
  // the eligible orders THEN, on explicit confirm, fire the send.
  const [backfillBusy, setBackfillBusy] = useState(false);
  const sendMissingReceipts = async () => {
    setBackfillBusy(true);
    try {
      const { data: preview } = await api.get("/admin/bulk-kit-orders/receipts/missing");
      const n = preview?.eligible_count || 0;
      if (n === 0) {
        toast.success("Every paid bulk kit order already has a receipt on file.");
        return;
      }
      const proceed = window.confirm(
        `${n} paid bulk kit order${n === 1 ? "" : "s"} ` +
        `${n === 1 ? "has" : "have"} never received a payment receipt.\n\n` +
        `Email a receipt to each partner now?\n\n` +
        `Receipts will use the amounts actually paid at the time of ` +
        `purchase (not today's pricing). Already-sent orders are skipped.`,
      );
      if (!proceed) return;
      const { data } = await api.post("/admin/bulk-kit-orders/receipts/send-missing", {});
      toast.success(
        `Backfill complete · sent ${data.sent_count}, ` +
        `skipped ${data.skipped_count}, failed ${data.failed_count}`,
      );
      reload();
    } catch (e) {
      toast.error(`Backfill failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBackfillBusy(false);
    }
  };

  return (
    <PortalShell>
      <div className="p-6 space-y-4" data-testid="admin-bulk-kit-orders-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Package className="w-6 h-6" /> PWS Bulk Kit Orders
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="admin-kit-orders-status-filter"
            className="h-9 rounded-md border border-slate-300 px-2 text-sm"
          >
            <option value="">All statuses</option>
            {["pending_payment","awaiting_labels","labels_generated","labels_failed","packing","shipped","delivered","cancelled","refunded"].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeCancelled}
              onChange={(e) => setIncludeCancelled(e.target.checked)}
              data-testid="admin-kit-orders-include-cancelled"
              className="rounded"
            />
            Show cancelled/refunded
          </label>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              data-testid="admin-kit-orders-include-archived"
              className="rounded"
            />
            Show archived
          </label>
          <Button variant="outline" size="sm" onClick={reload}>
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={sendMissingReceipts}
            disabled={backfillBusy}
            data-testid="admin-kit-orders-send-missing-receipts-btn"
            className="border-cyan-300 text-cyan-800 hover:bg-cyan-50"
            title="Preview + email receipts to every paid bulk kit order that never received one"
          >
            {backfillBusy
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <Send className="w-4 h-4 mr-1.5" />}
            Send missing receipts
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      ) : orders.length === 0 ? (
        <div className="p-8 text-center text-slate-500 border border-dashed border-slate-200 rounded-2xl">
          No orders matching this filter.
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-kit-orders-table">
            <thead className="text-xs text-slate-500 uppercase tracking-wider bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2">Order #</th>
                <th className="text-left px-4 py-2">Partner</th>
                <th className="text-left px-4 py-2">Boxes</th>
                <th className="text-left px-4 py-2">Kits</th>
                <th className="text-left px-4 py-2">Total</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Receipt</th>
                <th className="text-left px-4 py-2">Outbound</th>
                <th className="text-left px-4 py-2">Returns</th>
                <th className="text-left px-4 py-2">Ordered</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const isArchived  = !!o.archived_at;
                const isCancelled = o.status === "cancelled" || o.status === "refunded";
                return (
                <tr key={o.id}
                    data-testid={`admin-kit-order-row-${o.order_number}`}
                    className={`border-t border-slate-100 hover:bg-slate-50 ${
                      isArchived ? "opacity-60" : ""
                    }`}
                >
                  <td className="px-4 py-2 font-mono text-xs cursor-pointer" onClick={() => setSelected(o)}>{o.order_number}</td>
                  <td className="px-4 py-2 cursor-pointer" onClick={() => setSelected(o)}>{o.partner_snapshot?.business_name || o.partner_id}</td>
                  <td className="px-4 py-2">{o.boxes}</td>
                  <td className="px-4 py-2">{o.kits}</td>
                  <td className="px-4 py-2">{CENTS(o.total_cents)}</td>
                  <td className="px-4 py-2">
                    {o.status}
                    {isArchived && (
                      <span className="ml-1.5 text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-1.5 py-0.5">
                        archived
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {(() => {
                      const c = receiptChipLabel(o);
                      return (
                        <span
                          data-testid={`admin-kit-order-receipt-chip-${o.order_number}`}
                          className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded-full px-1.5 py-0.5 ${receiptChipClasses(c.tone)}`}
                          title={o.receipt_send_status === "failed"
                            ? (o.receipt_send_error || "Receipt send failed")
                            : (o.receipt_number ? `Receipt ${o.receipt_number}` : "No receipt yet")}
                        >
                          <Receipt className="w-3 h-3" /> {c.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2 font-mono text-[10px]">{o.outbound_tracking || "—"}</td>
                  <td className="px-4 py-2">{(o.return_labels || []).length}</td>
                  <td className="px-4 py-2 text-slate-500">{(o.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {isCancelled && !isArchived && (
                        <button
                          type="button"
                          data-testid={`admin-kit-order-archive-${o.order_number}`}
                          onClick={(e) => { e.stopPropagation(); archiveOrder(o.id); }}
                          title="Hide this cancelled order from the list"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-1.5 py-1 rounded"
                        >
                          <Archive className="w-3.5 h-3.5" /> Archive
                        </button>
                      )}
                      {isArchived && (
                        <button
                          type="button"
                          data-testid={`admin-kit-order-unarchive-${o.order_number}`}
                          onClick={(e) => { e.stopPropagation(); archiveOrder(o.id, true); }}
                          title="Restore this order to the active list"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-1.5 py-1 rounded"
                        >
                          <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setSelected(o)}
                        className="text-cyan-700 text-xs font-semibold hover:text-cyan-900"
                      >
                        Open →
                      </button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <OrderDrawer
          order={selected}
          onClose={() => setSelected(null)}
          onChanged={(row) => { setSelected(row); reload(); }}
        />
      )}
      </div>
    </PortalShell>
  );
}

function OrderDrawer({ order, onClose, onChanged }) {
  const [busy, setBusy] = useState("");
  // SCWT-BULK-KIT-RECEIPT-INLINE · Feb 2026 — Pre-populate the "Send
  // To" field for the inline receipt sender. Priority chain:
  //   1. Partner snapshot email (captured at purchase time).
  //   2. Live PWS partner record's email (fetched below).
  //   3. Blank — admin must type it in.
  const [receiptEmail, setReceiptEmail] = useState(
    order?.partner_snapshot?.email || "",
  );
  useEffect(() => {
    // Reset when a different order is opened.
    setReceiptEmail(order?.partner_snapshot?.email || "");
    // If snapshot is empty, fetch the live partner record and use
    // its email as the default. Silently ignored on failure — the
    // input just stays blank and the admin fills it in.
    if (!(order?.partner_snapshot?.email || "").trim() && order?.partner_id) {
      (async () => {
        try {
          const { data } = await api.get(
            `/admin/pws-partners/${order.partner_id}`,
          );
          const live = (data?.email || "").trim();
          if (live) setReceiptEmail(live);
        } catch { /* leave blank */ }
      })();
    }
  }, [order?.id, order?.partner_id, order?.partner_snapshot?.email]);
  const run = async (label, fn) => {
    setBusy(label);
    try {
      const { data } = await fn();
      toast.success(`${label} · done`);
      onChanged?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? (d?.message || label + " failed") : (d || label + " failed"));
    } finally { setBusy(""); }
  };

  const generate = () => run("Generate labels", () =>
    api.post(`/admin/bulk-kit-orders/${order.id}/generate-labels`));
  const markShipped = () => run("Mark shipped", () =>
    api.post(`/admin/bulk-kit-orders/${order.id}/mark-shipped`, {}));
  const cancel = () => {
    const reason = window.prompt("Cancellation reason?") || "";
    run("Cancel", () => api.post(`/admin/bulk-kit-orders/${order.id}/cancel`, { reason }));
  };
  const refund = () => {
    if (!window.confirm("Refund this order via Stripe? This is irreversible.")) return;
    const reason = window.prompt("Refund reason?") || "";
    run("Refund", () => api.post(`/admin/bulk-kit-orders/${order.id}/refund`, { reason }));
  };
  // SCWT-BULK-KIT-RESEND-PAY-LINK · Feb 2026 — mint a fresh Stripe
  // checkout session + email the partner. Only exposed while the
  // order is still `pending_payment` (declined card or abandoned
  // checkout).
  const resendPayLink = () => {
    const email = order?.partner_snapshot?.email || "the partner";
    if (!window.confirm(
      `Email a fresh Stripe payment link to ${email}?\n\n` +
      `A new checkout session will be created (the old one, if not ` +
      `already expired, remains valid too).`,
    )) return;
    run("Resend pay link", () =>
      api.post(`/admin/bulk-kit-orders/${order.id}/resend-payment-link`),
    );
  };
  // SCWT-BULK-KIT-RECEIPT · Feb 2026 — send (or resend) the Tax
  // Invoice for a bulk kit order. Reuses the same receipt number
  // and stored payment amounts.
  //
  // Recipient resolution:
  //   • If `overrideEmail` is provided (from the inline "Send To"
  //     input in the receipt panel), it wins and the backend also
  //     back-fills the partner_snapshot for future resends.
  //   • Otherwise the backend walks its fallback chain:
  //     snapshot → live partner record → skipped_no_email.
  //
  // The inline input is always visible in the drawer, so the admin
  // always has one clear place to type the address — no browser
  // popup, no DevTools required.
  const resendReceipt = async (overrideEmail = null) => {
    setBusy("Resend receipt");
    try {
      const { data } = await api.post(
        `/admin/bulk-kit-orders/${order.id}/resend-receipt`,
        overrideEmail ? { email: overrideEmail } : {},
      );
      if (data?.status === "sent") {
        toast.success(
          `Receipt ${data.receipt_number || ""} emailed to ${data.sent_to} ` +
          `(send #${data.receipt_send_count})`,
        );
      } else if (data?.status === "skipped_no_email") {
        // Backend fallback chain returned nothing usable. Guide the
        // admin to the inline input rather than throwing a wall of
        // technical error text at them.
        toast.error(
          "No email on file for this partner. " +
          "Type an address into the \"Send To\" box in the Payment " +
          "Receipt panel and click Send.",
          { duration: 8000 },
        );
      } else if (data?.status === "failed") {
        toast.error(`Receipt send failed: ${data.error || "unknown error"}`);
      } else {
        toast.info(`Receipt: ${data?.status || "no change"}`);
      }
      // Refresh the drawer with the freshly stamped order.
      try {
        const { data: fresh } = await api.get(`/admin/bulk-kit-orders/${order.id}`);
        onChanged?.(fresh);
      } catch { onChanged?.({ ...order }); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? (d?.message || "Resend receipt failed") : (d || "Resend receipt failed"));
    } finally { setBusy(""); }
  };
  // SCWT-BULK-TRACKING-RESYNC · Feb 2026 — retro-fill missing tracking
  // numbers by GET-ting each stored starshipit_order_id. Idempotent.
  const reconcile = () =>
    run("Reconcile tracking", () =>
      api.post(`/admin/bulk-kit-orders/${order.id}/reconcile-labels`),
    );
  // SCWT-BULK-SHIPPING-V2 · Feb 2026 — retry only labels that failed
  // (`starshipit_order_id === null` or `label_status === "failed"`).
  // Idempotent with Generate Labels — already-created labels are
  // reused verbatim, only missing ones re-hit Starshipit.
  const retryFailed = () =>
    run("Retry failed labels", () =>
      api.post(`/admin/bulk-kit-orders/${order.id}/retry-failed-labels`),
    );
  // Show the Reconcile button only when labels have been generated
  // AND at least one of them is missing tracking — otherwise the
  // action row gets noisy.
  const missingTracking =
    (order.outbound_shipments || []).some((s) => !s?.tracking) ||
    (order.return_labels     || []).some((r) => !r?.tracking);
  const canReconcile = missingTracking && ["labels_generated","packing","shipped","delivered"].includes(order.status);
  const bulkStatus = order.bulk_shipping_status;
  const hasFailedOrManual = ["partial_failed","manual_action_required"].includes(bulkStatus);
  const packingSheet = () => {
    // File download via the API base + admin auth header piggybacking.
    window.open(`${API_BASE}/api/admin/bulk-kit-orders/${order.id}/packing-sheet.pdf`, "_blank");
  };
  // SCWT-BULK-KIT-RECEIPT-PDF · Feb 2026 — download the accounting-
  // grade Tax Invoice PDF. Reuses the same receipt number the email
  // receipt uses (regenerated on demand from stored order values).
  const downloadTaxInvoice = () => {
    window.open(`${API_BASE}/api/admin/bulk-kit-orders/${order.id}/tax-invoice.pdf`, "_blank");
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end justify-end" onClick={onClose}>
      <div
        className="w-[520px] max-w-full h-full bg-white overflow-y-auto p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-kit-order-drawer"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Order</div>
            <div className="text-xl font-bold text-slate-900 font-mono">{order.order_number}</div>
            <div className="text-sm text-slate-600 mt-1">{order.partner_snapshot?.business_name}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="admin-kit-order-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-[10px] uppercase text-slate-500">Status</div>
            <div className="font-semibold">{order.status}</div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-[10px] uppercase text-slate-500">Total</div>
            <div className="font-semibold">{CENTS(order.total_cents)}</div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-[10px] uppercase text-slate-500">Boxes / Kits</div>
            <div className="font-semibold">{order.boxes} / {order.kits}</div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-[10px] uppercase text-slate-500">Outbound tracking</div>
            <div className="font-mono text-[11px]">{order.outbound_tracking || "—"}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {order.status === "pending_payment" && (
            <Button
              onClick={resendPayLink}
              disabled={!!busy}
              data-testid="admin-kit-order-resend-pay-link-btn"
              className="bg-cyan-700 text-white hover:bg-cyan-800 h-9"
              title="Email the partner a fresh Stripe checkout link"
            >
              {busy === "Resend pay link" ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <DollarSign className="w-4 h-4 mr-1.5" />
              )}
              Resend pay link
            </Button>
          )}
          {order.status === "awaiting_labels" || order.status === "labels_failed" ? (
            <Button onClick={generate} disabled={!!busy} data-testid="admin-kit-order-generate-labels-btn" className="bg-slate-900 text-white hover:bg-cyan-700 h-9">
              {busy === "Generate labels" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Printer className="w-4 h-4 mr-1.5" />}
              Generate labels
            </Button>
          ) : null}
          {order.status === "labels_generated" || order.status === "packing" ? (
            <Button onClick={markShipped} disabled={!!busy} data-testid="admin-kit-order-mark-shipped-btn" className="bg-emerald-600 text-white hover:bg-emerald-700 h-9">
              <Truck className="w-4 h-4 mr-1.5" /> Mark shipped
            </Button>
          ) : null}
          <Button onClick={packingSheet} variant="outline" data-testid="admin-kit-order-packing-sheet-btn" className="h-9">
            <Download className="w-4 h-4 mr-1.5" /> Packing sheet
          </Button>
          {/* Download the A4 Tax Invoice PDF — generated on-demand from
              the immutable stored transaction values. Available for
              every paid-or-later order regardless of email-send state. */}
          {["awaiting_labels","labels_generated","labels_failed","packing","shipped","delivered","refunded"].includes(order.status) && (
            <Button
              onClick={downloadTaxInvoice}
              variant="outline"
              data-testid="admin-kit-order-download-tax-invoice-btn"
              className="h-9 border-slate-300 text-slate-700 hover:bg-slate-50"
              title="Download A4 Tax Invoice PDF for Xero / MYOB"
            >
              <Download className="w-4 h-4 mr-1.5" /> Tax Invoice PDF
            </Button>
          )}
          {/* Receipt resend is available on any paid-or-later order.
              For older orders that never received a receipt, this is
              the same button — the backend does the right thing. */}
          {["awaiting_labels","labels_generated","labels_failed","packing","shipped","delivered","refunded"].includes(order.status) && (
            <Button
              onClick={() => resendReceipt(receiptEmail.trim() || null)}
              disabled={!!busy}
              variant="outline"
              data-testid="admin-kit-order-resend-receipt-btn"
              className="h-9 border-cyan-300 text-cyan-800 hover:bg-cyan-50"
              title={order.receipt_sent_at
                ? "Resend the payment receipt (uses the Send To email below)"
                : "Send the payment receipt for the first time (uses the Send To email below)"}
            >
              {busy === "Resend receipt"
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <Receipt className="w-4 h-4 mr-1.5" />}
              {order.receipt_sent_at ? "Resend receipt" : "Send receipt"}
            </Button>
          )}
          <Button
            onClick={() => window.open(`/admin/bulk-kit-orders/${order.id}/packing`, "_self")}
            variant="outline"
            data-testid="admin-kit-order-open-packing-screen-btn"
            className="h-9"
          >
            <Package className="w-4 h-4 mr-1.5" /> Open packing screen
          </Button>
          {!["cancelled","refunded","shipped","delivered"].includes(order.status) && (
            <Button onClick={cancel} disabled={!!busy} variant="outline" data-testid="admin-kit-order-cancel-btn" className="h-9 text-rose-700">
              <Ban className="w-4 h-4 mr-1.5" /> Cancel
            </Button>
          )}
          {order.stripe_payment_intent && order.status !== "refunded" && (
            <Button onClick={refund} disabled={!!busy} variant="outline" data-testid="admin-kit-order-refund-btn" className="h-9 text-rose-700">
              <DollarSign className="w-4 h-4 mr-1.5" /> Refund
            </Button>
          )}
          {canReconcile && (
            <Button
              onClick={reconcile}
              disabled={!!busy}
              variant="outline"
              data-testid="admin-kit-order-reconcile-tracking-btn"
              className="h-9 text-amber-700 border-amber-300 hover:bg-amber-50"
              title="Fetch missing tracking numbers from Starshipit"
            >
              {busy === "Reconcile tracking" ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Truck className="w-4 h-4 mr-1.5" />
              )}
              Reconcile tracking
            </Button>
          )}
          {hasFailedOrManual && (
            <Button
              onClick={retryFailed}
              disabled={!!busy}
              variant="outline"
              data-testid="admin-kit-order-retry-failed-labels-btn"
              className="h-9 text-rose-700 border-rose-300 hover:bg-rose-50"
              title="Only re-hit Starshipit for labels that failed. Successful labels are reused."
            >
              {busy === "Retry failed labels" ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Printer className="w-4 h-4 mr-1.5" />
              )}
              Retry failed labels
            </Button>
          )}
        </div>

        {/* SCWT-BULK-SHIPPING-V2 · Feb 2026 — bulk shipping status
            roll-up chip. Hidden for orders that haven't reached
            label generation. */}
        {bulkStatus && (
          <div
            className="rounded-xl border p-3 text-xs"
            data-testid="admin-kit-order-bulk-shipping-status"
            style={{
              borderColor:
                bulkStatus === "complete" ? "#10b981" :
                bulkStatus === "partial_failed" ? "#f43f5e" :
                bulkStatus === "manual_action_required" ? "#f59e0b" :
                "#cbd5e1",
              background:
                bulkStatus === "complete" ? "#ecfdf5" :
                bulkStatus === "partial_failed" ? "#fff1f2" :
                bulkStatus === "manual_action_required" ? "#fffbeb" :
                "#f8fafc",
            }}
          >
            <div className="uppercase text-[10px] text-slate-500 font-semibold tracking-wider">
              Bulk shipping status
            </div>
            <div className="font-semibold text-slate-800 mt-0.5">
              {bulkStatus.replace(/_/g, " ")}
            </div>
            {bulkStatus === "partial_failed" && (
              <div className="text-slate-600 mt-1">
                One or more labels failed to create. Click <b>Retry failed labels</b>
                {" "}to re-hit Starshipit only for the failed ones — successful
                labels are reused so you never double-bill AusPost.
              </div>
            )}
            {bulkStatus === "manual_action_required" && (
              <div className="text-slate-600 mt-1">
                Starshipit accepted the order but returned no tracking. Click
                {" "}<b>Reconcile tracking</b> to try to pull it, or check the
                Starshipit dashboard for a manifest problem.
              </div>
            )}
          </div>
        )}

        <div data-testid="admin-kit-order-receipt-panel" className="rounded-xl border border-slate-200 bg-white p-3 text-xs">
          <div className="flex items-center justify-between">
            <div className="uppercase text-[10px] text-slate-500 font-semibold tracking-wider flex items-center gap-1.5">
              <Receipt className="w-3.5 h-3.5" /> Payment receipt
            </div>
            {(() => {
              const c = receiptChipLabel(order);
              return (
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded-full px-1.5 py-0.5 ${receiptChipClasses(c.tone)}`}>
                  {c.label}
                </span>
              );
            })()}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2 text-slate-600">
            <div>
              <div className="text-[10px] text-slate-500 uppercase">Receipt no.</div>
              <div className="font-mono text-[11px]">{order.receipt_number || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 uppercase">Sent to</div>
              <div>{order.receipt_sent_to || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 uppercase">First sent</div>
              <div>{(order.receipt_sent_at || "").slice(0, 16).replace("T", " ") || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 uppercase">Send count</div>
              <div>{order.receipt_send_count || 0}</div>
            </div>
          </div>
          {order.receipt_send_error && (
            <div className="mt-2 text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
              <b>Send error:</b> {order.receipt_send_error}
            </div>
          )}
          {/* SCWT-BULK-KIT-RECEIPT-INLINE · Feb 2026 — always-visible
              inline "send to" input. Pre-populates from whatever email
              we know about the partner (snapshot → live record →
              blank) and lets the admin type/edit it before hitting
              Send. Bypasses any browser popup blocker or DevTools
              detective work: what you see is what gets sent. */}
          <div className="mt-3 flex items-end gap-2 pt-3 border-t border-slate-100">
            <div className="flex-1 min-w-0">
              <label
                htmlFor={`receipt-email-${order.id}`}
                className="block text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1"
              >
                Send To
              </label>
              <input
                id={`receipt-email-${order.id}`}
                data-testid="admin-kit-order-receipt-email-input"
                type="email"
                value={receiptEmail}
                onChange={(e) => setReceiptEmail(e.target.value)}
                placeholder="partner@example.com"
                className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
              />
            </div>
            <Button
              onClick={() => resendReceipt(receiptEmail.trim() || null)}
              disabled={!!busy || !receiptEmail.trim()}
              size="sm"
              data-testid="admin-kit-order-receipt-inline-send-btn"
              className="h-8 shrink-0 bg-cyan-700 hover:bg-cyan-800 text-white"
              title="Email the Tax Invoice to this address (also back-fills the order if empty)"
            >
              {busy === "Resend receipt"
                ? <Loader2 className="w-3.5 h-3.5" />
                : <><Send className="w-3.5 h-3.5 mr-1" />Send</>}
            </Button>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase text-slate-500 mb-1">Return labels ({(order.return_labels || []).length})</div>
          {(order.return_labels || []).length > 0 ? (
            <table className="w-full text-xs">
              <tbody>
                {order.return_labels.map((r) => (
                  <tr key={r.kit_index} className="border-b border-slate-100">
                    <td className="py-1 pr-2">#{r.kit_index}</td>
                    <td className="py-1 font-mono">{r.tracking || "—"}</td>
                    <td className="py-1"><a href={r.label_url} target="_blank" rel="noreferrer" className="text-cyan-700">label</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-xs text-slate-500">No labels generated yet.</div>
          )}
        </div>

        <div>
          <div className="text-xs uppercase text-slate-500 mb-1">Audit</div>
          <ul className="text-xs space-y-1">
            {(order.audit || []).map((a, i) => (
              <li key={i} className="text-slate-600">
                <span className="font-mono">{(a.ts || "").slice(0, 16)}</span> ·
                <b> {a.event}</b> · {a.actor_email}
                {a.note && <> · {a.note}</>}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
